var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo } from 'react';
import { t } from 'i18next';
import Popup from '../../components/Popup';
import '../../styles/widgets/popup/manipulativePopup.scss';
import DiceManipulativePopup from './DiceManipulativePopup';
import { DataContext } from '../../player/DataContext';
import { v4 as uuidv4 } from 'uuid';
import CardsManipulativePopup from './CardsManipulativePopup';
import NumberChartManipulativePopup from './NumberChartManipulativePopup';
import ClockManipulativePopup from './Clock/ClockManipulativePopup';
import NumberChartGame from './NumberChartGame';
import globalStore from '../../store';
import { saveState } from '../../helper/stateManagerHelper';
import NumberFramesManipulativePopup from './NumberFramesManipulativePopup';
import ArrayModelManipulativePopup from './ArrayModelManipulativePopup';
import PatternShapesManipulativePopup, { PatternOutlineConfirmationPopup, PatternClearOutlineConfirmationPopup, } from './PatternShapesManipulativePopup';
import FractionTilesManipulativePopup from './FractionTilesManipulativePopup';
import NumberAndMoneyPiecePopup from './NumberAndMoneyPiecePopup';
import { NMP_PIECE_TYPE_CONFIG } from '../../constants/nmpConstants';
import customEventHandler from '../../helper/customEvents';
import ChartSizeAlertPopup from './ChartSizeAlertPopup';
import ClockToolAlertPopup from './ClockToolAlertPopup';
import SpinnerManipulativePopup from './SpinnerManipulativePopup';
import { getFractionDimensions, sumAndReduceFractions } from '../../helper/helper';
import NumberLineManipulativePopup from './NumberLineManipulativePopup';
import ColoredTilesManipulativePopup from './ColoredTilesManipulativePopup';
import CoinsAndCountersManipulativePopup from './CoinsAndCountersManipulativePopup';
import { getVisibleMidpoint } from '../../helper/viewportHelper';
var shapeSize = {
    rectangle: {
        width: 660,
        height: 55,
    },
    circle: {
        width: 300,
        height: 300,
    },
};
var ManipulativePopup = function (props) {
    var _a;
    var _b = useContext(DataContext), setManipulative = _b.setManipulative, manipulative = _b.manipulative, setManiplulatorComponentVisible = _b.setManiplulatorComponentVisible, selectedManipulativeId = _b.selectedManipulativeId, maniplulatorComponentVisible = _b.maniplulatorComponentVisible, activeManipulator = _b.activeManipulator, setSelectedManipulativeId = _b.setSelectedManipulativeId, setActiveManipulator = _b.setActiveManipulator, setSelectedTool = _b.setSelectedTool, selectedTool = _b.selectedTool, getPositionForNewItem = _b.getPositionForNewItem, popupReturnFocusRef = _b.popupReturnFocusRef;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var selectedItem = useMemo(function () {
        var found = manipulative.find(function (m) { return m.data.id === selectedManipulativeId; });
        if ((found === null || found === void 0 ? void 0 : found.data.type) === 'fractionTiles' && activeManipulator !== 'fractionTiles')
            return undefined;
        // A stale selectedManipulativeId can still point at a ColoredTiles tile/group
        // (its click handler sets selectedManipulativeId on select and never clears it),
        // so opening the clock popup would otherwise read clock fields off ColoredTiles
        // data and render "Na:Na". Only accept a match whose type the clock actually uses.
        if (activeManipulator === 'clocks' &&
            (found === null || found === void 0 ? void 0 : found.data.type) !== 'analog' &&
            (found === null || found === void 0 ? void 0 : found.data.type) !== 'timer')
            return undefined;
        return found;
    }, [manipulative, selectedManipulativeId, activeManipulator]);
    var effectiveSelectedId = selectedItem != null ? selectedManipulativeId : '';
    useEffect(function () {
        if (activeManipulator === 'patternShapes') {
            var _toBeUpdated = void 0;
            var _a = getVisibleMidpoint(zoomLevel), x = _a.x, y = _a.y;
            _toBeUpdated = {
                type: 'patternShapes',
                data: {
                    id: "patternShapes_".concat(uuidv4().replace(/-/g, '')),
                    type: 'patternShapes',
                    shapeType: 'square',
                    center: { x: x, y: y },
                    scale: 1,
                    rotation: 0,
                    color: 'BLUE',
                    lock: {
                        positioning: false,
                        editing: false,
                    },
                },
                widgetId: "patternShapes:".concat(uuidv4().replace(/-/g, '')),
            };
            saveState({
                type: _toBeUpdated.data.id,
                id: _toBeUpdated.data.id.split('_')[1],
                propertyName: 'create',
                old: JSON.stringify(_toBeUpdated),
            });
            var _capturedPatternShape_1 = _toBeUpdated;
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [__assign({}, _capturedPatternShape_1)], false); });
            setSelectedManipulativeId('');
            setManiplulatorComponentVisible(false);
            setActiveManipulator('');
        }
    }, [activeManipulator]);
    // Spinner, clock, numberChart, and numberLine popups read selectedManipulativeId directly
    // from context. If it still points to a fraction tile when a different popup opens, clear it
    // so those popups enter create-new mode instead of trying to edit the fraction tile.
    useEffect(function () {
        if (selectedManipulativeId !== '' && effectiveSelectedId === '') {
            setSelectedManipulativeId('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeManipulator]);
    var renderPopup = function () {
        var _a;
        switch (activeManipulator) {
            case 'dice':
                var _selectedData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                return (_jsx(DiceManipulativePopup, { diceType: _selectedData === null || _selectedData === void 0 ? void 0 : _selectedData.diceType, faces: _selectedData === null || _selectedData === void 0 ? void 0 : _selectedData.faces, lock: _selectedData === null || _selectedData === void 0 ? void 0 : _selectedData.lock, activeFaceIndex: _selectedData === null || _selectedData === void 0 ? void 0 : _selectedData.activeFaceIndex, color: _selectedData === null || _selectedData === void 0 ? void 0 : _selectedData.color, onSubmit: function (data) {
                        var _a, _b, _c, _d;
                        var _existingDice = effectiveSelectedId !== ''
                            ? manipulative.find(function (item) { return item.data.id === selectedManipulativeId && item.data.type === 'dice'; })
                            : undefined;
                        if (_existingDice) {
                            var _toBeUpdated_1 = __assign(__assign({}, _existingDice), { data: __assign(__assign({}, _existingDice.data), { diceType: data.diceType, faces: __spreadArray([], data.faces, true), lock: {
                                        positioning: (_b = (_a = _existingDice.data.lock) === null || _a === void 0 ? void 0 : _a.positioning) !== null && _b !== void 0 ? _b : false,
                                        editing: (_d = (_c = _existingDice.data.lock) === null || _c === void 0 ? void 0 : _c.editing) !== null && _d !== void 0 ? _d : false,
                                    } }) });
                            saveState({
                                type: selectedManipulativeId,
                                id: _toBeUpdated_1.data.id.split('_')[1],
                                propertyName: 'completeData',
                                old: JSON.stringify(_existingDice),
                                new: JSON.stringify(_toBeUpdated_1),
                            });
                            setManipulative(function (prev) {
                                return prev.map(function (item) { return (item.data.id === selectedManipulativeId ? _toBeUpdated_1 : item); });
                            });
                            // Auto-select edited dice so focus moves to it
                            setSelectedTool([effectiveSelectedId]);
                        }
                        else {
                            var _e = getPositionForNewItem(), x = _e.x, y = _e.y;
                            var newDiceId = "dice_".concat(uuidv4().replace(/-/g, ''));
                            var _toBeUpdated_2 = {
                                type: 'dice',
                                data: {
                                    id: newDiceId,
                                    type: 'dice',
                                    diceType: data.diceType,
                                    center: { x: x, y: y },
                                    dimensions: { width: 96, height: 89 },
                                    defaultDimensions: { width: 96, height: 89 },
                                    scale: 1,
                                    faces: data.faces,
                                    activeFaceIndex: 1,
                                    color: 'WHITE',
                                    lock: {
                                        positioning: false,
                                        editing: false,
                                    },
                                    isPreset: false,
                                },
                                widgetId: "dice_".concat(uuidv4().replace(/-/g, '')),
                            };
                            saveState({
                                type: _toBeUpdated_2.data.id,
                                id: _toBeUpdated_2.data.id.split('_')[1],
                                propertyName: 'create',
                                old: JSON.stringify(_toBeUpdated_2),
                            });
                            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [_toBeUpdated_2], false); });
                            // Auto-select the newly created dice
                            setSelectedTool([_toBeUpdated_2.data.id]);
                        }
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    }, onCancel: function () {
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    } }));
            case 'numberCharts':
                var numChartdata = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                return _jsx(NumberChartManipulativePopup, { data: numChartdata });
            case 'cards':
                var _cardsData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                return (_jsx(CardsManipulativePopup, { rotate: _cardsData === null || _cardsData === void 0 ? void 0 : _cardsData.rotate, cards: _cardsData === null || _cardsData === void 0 ? void 0 : _cardsData.cards, deckType: _cardsData === null || _cardsData === void 0 ? void 0 : _cardsData.deckType, deckIdx: _cardsData === null || _cardsData === void 0 ? void 0 : _cardsData.deckIdx, gradeIdx: _cardsData === null || _cardsData === void 0 ? void 0 : _cardsData.gradeIdx, onSubmit: function (data) {
                        var _a, _b, _c, _d;
                        var _toBeUpdated;
                        var _existingCard = effectiveSelectedId !== ''
                            ? manipulative.find(function (m) { return m.data.id === effectiveSelectedId && m.data.type === 'deck'; })
                            : undefined;
                        if (_existingCard) {
                            var _newItem = _existingCard;
                            var existingDeckId_1 = _newItem.data.id;
                            _toBeUpdated = __assign(__assign({}, _newItem), { data: __assign(__assign({}, _newItem.data), { cards: data.cards.map(function (card) { return (__assign(__assign({}, card), { deckId: existingDeckId_1 })); }), deckType: data.deckType, rotate: data.rotate, deckIdx: data.deckIdx, gradeIdx: data.gradeIdx, dimensions: data.rotate
                                        ? { width: 301, height: 215 }
                                        : { width: 215, height: 301 }, lock: {
                                        positioning: (_b = (_a = _newItem.data.lock) === null || _a === void 0 ? void 0 : _a.positioning) !== null && _b !== void 0 ? _b : false,
                                        editing: (_d = (_c = _newItem.data.lock) === null || _c === void 0 ? void 0 : _c.editing) !== null && _d !== void 0 ? _d : false,
                                    } }) });
                            saveState({
                                type: selectedManipulativeId,
                                id: _toBeUpdated.data.id.split('_')[1],
                                propertyName: 'completeData',
                                old: JSON.stringify(_newItem),
                                new: JSON.stringify(_toBeUpdated),
                            });
                        }
                        else {
                            var _e = getPositionForNewItem(), cardX = _e.x, cardY = _e.y;
                            var newCardId_1 = "deck_".concat(uuidv4().replace(/-/g, ''));
                            _toBeUpdated = {
                                type: 'deck_pool',
                                data: {
                                    id: newCardId_1,
                                    type: 'deck',
                                    cards: data.cards.map(function (card) { return (__assign(__assign({}, card), { deckId: newCardId_1 })); }),
                                    deckType: data.deckType,
                                    rotate: data.rotate,
                                    deckIdx: data.deckIdx,
                                    gradeIdx: data.gradeIdx,
                                    center: { x: cardX, y: cardY },
                                    dimensions: data.rotate
                                        ? { width: 301, height: 215 }
                                        : { width: 215, height: 301 },
                                    scale: 1,
                                    lock: {
                                        positioning: false,
                                        editing: false,
                                    },
                                    isPreset: false,
                                },
                                widgetId: 'cards:1',
                            };
                            saveState({
                                type: _toBeUpdated.data.id,
                                id: _toBeUpdated.data.id.split('_')[1],
                                propertyName: 'create',
                                old: JSON.stringify(_toBeUpdated),
                            });
                        }
                        setManipulative(function (prev) {
                            if (_existingCard) {
                                return prev.map(function (m) { return (m.data.id === effectiveSelectedId ? _toBeUpdated : m); });
                            }
                            else {
                                return __spreadArray(__spreadArray([], prev, true), [_toBeUpdated], false);
                            }
                        });
                        // Auto-select new or edited card (moved outside updater — side effects inside updaters are unreliable)
                        if (_existingCard) {
                            setSelectedTool([effectiveSelectedId]);
                        }
                        else {
                            setSelectedTool([_toBeUpdated.data.id]);
                        }
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    }, onCancel: function () {
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    } }));
            case 'clocks':
                var cdata = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                return _jsx(ClockManipulativePopup, { data: cdata });
            case 'numberChartGame':
                return _jsx(NumberChartGame, {});
            case 'chartSizeAlert':
                return _jsx(ChartSizeAlertPopup, {});
            case 'clockToolAlert':
                return _jsx(ClockToolAlertPopup, {});
            case 'numberFrames': {
                var nfData_1 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                var initialConfig = nfData_1
                    ? {
                        frameType: nfData_1.frameType,
                        rows: nfData_1.rows,
                        cols: nfData_1.cols,
                    }
                    : undefined;
                return (_jsx(NumberFramesManipulativePopup, { initialConfig: initialConfig, onSubmit: function (cfg) {
                        var _a = getVisibleMidpoint(zoomLevel), x = _a.x, y = _a.y;
                        if (effectiveSelectedId) {
                            // update existing — clear all placed tokens and their counters
                            if (nfData_1) {
                                saveState({
                                    type: effectiveSelectedId,
                                    id: effectiveSelectedId,
                                    propertyName: 'completeData',
                                    old: JSON.stringify(nfData_1),
                                    new: JSON.stringify(__assign(__assign({}, nfData_1), { frameType: cfg.frameType, rows: cfg.rows, cols: cfg.cols, tokens: {} })),
                                });
                            }
                            setManipulative(function (prev) {
                                return prev
                                    .filter(function (item) {
                                    return !(item.data.type === 'numberFrameCounter' &&
                                        item.data.frameId === selectedManipulativeId);
                                })
                                    .map(function (item) {
                                    return item.data.id === selectedManipulativeId
                                        ? __assign(__assign({}, item), { type: 'numberFrames', data: __assign(__assign({}, item.data), { type: 'numberFrames', frameType: cfg.frameType, rows: cfg.rows, cols: cfg.cols, tokens: {} }) }) : item;
                                });
                            });
                        }
                        else {
                            // create new
                            var cellSize = 30; // logical units; adjust as you like
                            var width = cfg.cols * cellSize;
                            var height = cfg.rows * cellSize;
                            var newItem_1 = {
                                type: 'numberFrames',
                                widgetId: "numberFrames:".concat(uuidv4().replace(/-/g, '')),
                                data: {
                                    id: "numberFrame_".concat(uuidv4().replace(/-/g, '')),
                                    type: 'numberFrames',
                                    frameType: cfg.frameType,
                                    rows: cfg.rows,
                                    cols: cfg.cols,
                                    center: { x: x, y: y },
                                    dimensions: { width: width, height: height },
                                    scale: 1,
                                    lock: { positioning: false, editing: false },
                                },
                            };
                            saveState({
                                type: newItem_1.data.id,
                                id: newItem_1.data.id.split('_')[1],
                                propertyName: 'create',
                                old: JSON.stringify(newItem_1),
                            });
                            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newItem_1], false); });
                            setSelectedTool([newItem_1.data.id]);
                            // After the popup closes and React mounts the new frame,
                            // park focus on its wrapper. The delay is longer than the
                            // Popup cleanup's 0 ms focus-restore so this wins.
                            var newFrameId_1 = newItem_1.data.id;
                            setTimeout(function () {
                                var el = document.querySelector("[data-manipulative-id=\"".concat(newFrameId_1, "\"]"));
                                el === null || el === void 0 ? void 0 : el.focus();
                            }, 200);
                        }
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    }, onCancel: function () {
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    } }));
            }
            case 'arrayModels': {
                var arrayData = ((_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _a === void 0 ? void 0 : _a.type) === 'arrayModels' ? selectedItem.data : undefined;
                return _jsx(ArrayModelManipulativePopup, { data: arrayData });
            }
            case 'coloredTiles': {
                return _jsx(ColoredTilesManipulativePopup, {});
            }
            case 'coinsAndCounters': {
                return _jsx(CoinsAndCountersManipulativePopup, {});
            }
            case 'fractionTiles': {
                var _selectedData_1 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                return (_jsx(FractionTilesManipulativePopup, { onSubmit: function (data) {
                        var selectedIds = data.selectedIds, isMultiple = data.isMultiple;
                        var _newManipulativeState;
                        var oldData = [];
                        var newData = [];
                        if (effectiveSelectedId !== '' || isMultiple) {
                            var isCircleToRect_1 = data.shapeType === 'rectangle' &&
                                manipulative.some(function (m) {
                                    return selectedIds.includes(m.data.id || '') &&
                                        m.data.shapeType === 'circle';
                                });
                            var groups_1 = {};
                            if (isCircleToRect_1) {
                                manipulative.forEach(function (item) {
                                    var itemId = item.data.id || '';
                                    if (selectedIds.includes(itemId)) {
                                        var d = item.data;
                                        var key = "".concat(d.center.x, ",").concat(d.center.y);
                                        if (!groups_1[key])
                                            groups_1[key] = [];
                                        groups_1[key].push(d);
                                    }
                                });
                            }
                            _newManipulativeState = manipulative.map(function (item) {
                                var _a, _b, _c, _d;
                                var itemId = item.data.id || '';
                                if (selectedIds.includes(itemId)) {
                                    oldData.push(JSON.parse(JSON.stringify(item)));
                                    var d = item.data;
                                    var fractionSize = d.gluedFrom && d.gluedFrom.length > 0
                                        ? sumAndReduceFractions(d.gluedFrom.map(function (f) { return f.fractionSize; }))
                                        : d.fractionSize;
                                    var config = getFractionDimensions(fractionSize);
                                    var dimensions = data.shapeType === 'rectangle'
                                        ? (config === null || config === void 0 ? void 0 : config.rectangle) || shapeSize.rectangle
                                        : shapeSize.circle;
                                    var shapeType = data.shapeType, lablesType = data.lablesType, customLabel = data.customLabel, individualLabels = data.individualLabels;
                                    var center = d.center;
                                    if (isCircleToRect_1) {
                                        var key = "".concat(d.center.x, ",").concat(d.center.y);
                                        var group = groups_1[key];
                                        if (group && group.length > 1) {
                                            // Sort by rotation to maintain relative sequence
                                            var sortedGroup = __spreadArray([], group, true).sort(function (a, b) { return (a.rotation || 0) - (b.rotation || 0); });
                                            var myIdx = sortedGroup.findIndex(function (g) { return g.id === itemId; });
                                            // Calculate offset
                                            var xOffset = 0;
                                            for (var i = 0; i < myIdx; i++) {
                                                var prevFractionSize = sortedGroup[i].gluedFrom && sortedGroup[i].gluedFrom.length > 0
                                                    ? sumAndReduceFractions(sortedGroup[i].gluedFrom.map(function (f) { return f.fractionSize; }))
                                                    : sortedGroup[i].fractionSize;
                                                var prevDim = ((_a = getFractionDimensions(prevFractionSize)) === null || _a === void 0 ? void 0 : _a.rectangle) ||
                                                    shapeSize.rectangle;
                                                xOffset += prevDim.width;
                                            }
                                            center = { x: d.center.x + xOffset, y: d.center.y };
                                        }
                                    }
                                    var updatedItem = __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { shapeType: shapeType, lablesType: lablesType, customLabel: (_b = individualLabels[itemId]) !== null && _b !== void 0 ? _b : customLabel, dimensions: dimensions, defaultDimensions: dimensions, fractionSize: fractionSize, center: center, lock: {
                                                positioning: !!((_c = item.data.lock) === null || _c === void 0 ? void 0 : _c.positioning),
                                                editing: !!((_d = item.data.lock) === null || _d === void 0 ? void 0 : _d.editing),
                                            } }), (d.gluedFrom ? { gluedFrom: [] } : {})) });
                                    newData.push(JSON.parse(JSON.stringify(updatedItem)));
                                    return updatedItem;
                                }
                                return item;
                            });
                            if (oldData.length > 0) {
                                if (oldData.length === 1) {
                                    saveState({
                                        type: oldData[0].data.id,
                                        id: oldData[0].data.id.split('_')[1],
                                        propertyName: 'completeData',
                                        old: JSON.stringify(oldData[0]),
                                        new: JSON.stringify(newData[0]),
                                    });
                                }
                                else {
                                    saveState({
                                        type: 'multipleManipulatives',
                                        id: uuidv4().replace(/-/g, ''),
                                        propertyName: 'multipleCompleteData',
                                        old: JSON.stringify(oldData),
                                        new: JSON.stringify(newData),
                                    });
                                }
                            }
                        }
                        else {
                            // Don't place a tile — open the toolbar so user can drag pieces out.
                            var dummyId = "fractionTiles_".concat(data.shapeType, "_").concat(data.lablesType, "_dummyForRightTool");
                            setSelectedTool([dummyId]);
                            _newManipulativeState = manipulative;
                        }
                        setManipulative(_newManipulativeState);
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    }, onCancel: function () {
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    }, editDetails: _selectedData_1 }, selectedManipulativeId || 'new'));
            }
            case 'moneyPieces': {
                // Only pass configureItems when opened via edit button (selectedManipulativeId is set)
                var isEditMode = !!effectiveSelectedId;
                var selectedNmpItems = isEditMode
                    ? selectedTool
                        .map(function (id) { return manipulative.find(function (m) { return m.data.id === id; }); })
                        .filter(function (m) {
                        return !!m && (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece');
                    })
                    : [];
                return (_jsx(NumberAndMoneyPiecePopup, { configureItems: selectedNmpItems.length > 0 ? selectedNmpItems : undefined, onDone: function (pieces, deleteIds, pieceTypeHint) {
                        var _a, _b, _c, _d, _e;
                        if (deleteIds) {
                            // Configure mode: reconcile updates, creates, deletes
                            var existingIds_1 = new Set(manipulative.map(function (m) { return m.data.id; }));
                            var statesToSave_1 = [];
                            deleteIds.forEach(function (id) {
                                var item = manipulative.find(function (m) { return m.data.id === id; });
                                if (item) {
                                    statesToSave_1.push({
                                        type: id,
                                        id: id,
                                        propertyName: 'delete',
                                        old: JSON.stringify(item),
                                    });
                                }
                            });
                            pieces.forEach(function (piece) {
                                var pieceId = piece.data.id;
                                if (existingIds_1.has(pieceId)) {
                                    var oldItem = manipulative.find(function (m) { return m.data.id === pieceId; });
                                    if (oldItem) {
                                        statesToSave_1.push({
                                            type: pieceId,
                                            id: pieceId.split('_')[1],
                                            propertyName: 'completeData',
                                            old: JSON.stringify(oldItem),
                                            new: JSON.stringify(piece),
                                        });
                                    }
                                }
                            });
                            var newItems_1 = pieces.filter(function (p) { return !existingIds_1.has(p.data.id); });
                            if (newItems_1.length > 0) {
                                var newFirstId = newItems_1[0].data.id;
                                statesToSave_1.push({
                                    type: newFirstId,
                                    id: newFirstId.split('_')[1],
                                    propertyName: 'batchCreate',
                                    old: JSON.stringify(newItems_1),
                                });
                            }
                            if (statesToSave_1.length > 0) {
                                saveState(statesToSave_1);
                            }
                            setManipulative(function (prev) {
                                var result = prev.filter(function (m) { return !deleteIds.includes(m.data.id); });
                                result = result.map(function (m) {
                                    var updated = pieces.find(function (p) { return p.data.id === m.data.id; });
                                    return updated || m;
                                });
                                return __spreadArray(__spreadArray([], result, true), newItems_1, true);
                            });
                            var pieceTypeConfigure = (_b = (_a = pieces[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.pieceType;
                            var configKeyConfigure = pieceTypeConfigure
                                ? NMP_PIECE_TYPE_CONFIG[pieceTypeConfigure]
                                : null;
                            setSelectedTool(configKeyConfigure ? ["".concat(configKeyConfigure, "_dummyForRightTool")] : []);
                        }
                        else {
                            // Create mode: batch create. May be an empty `pieces` list if
                            // the user hit Done with zero count — in that case we still
                            // want to route them into the right-drawer config for the
                            // currently-selected material (via the pieceTypeHint).
                            if (pieces.length > 0) {
                                var firstId = pieces[0].data.id;
                                saveState({
                                    type: firstId,
                                    id: firstId.split('_')[1],
                                    propertyName: 'batchCreate',
                                    old: JSON.stringify(pieces),
                                });
                                setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), pieces, true); });
                            }
                            var pieceType = (_e = (_d = (_c = pieces[0]) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.pieceType) !== null && _e !== void 0 ? _e : pieceTypeHint;
                            var configKey = pieceType ? NMP_PIECE_TYPE_CONFIG[pieceType] : null;
                            if (pieces.length > 0) {
                                // Select the freshly-placed pieces so the right panel
                                // immediately shows their per-piece actions (Exchange,
                                // Flip, animation toggle) without requiring a re-click.
                                // Prefer a groupId when present so we treat the group as
                                // a single logical selection.
                                var groupIds = Array.from(new Set(pieces
                                    .map(function (p) { return p.data.groupId; })
                                    .filter(function (g) { return !!g; })));
                                var selection = groupIds.length > 0
                                    ? groupIds
                                    : pieces.map(function (p) { return p.data.id; });
                                setSelectedTool(selection);
                            }
                            else {
                                setSelectedTool(configKey ? ["".concat(configKey, "_dummyForRightTool")] : []);
                            }
                            if (pieces.length === 0 && configKey) {
                                // No pieces placed → force-open the right drawer so the
                                // user lands in the config they asked for, then move focus
                                // to the drawer's toggle button (collapse / expand). From
                                // there the user can Tab into the panel's content if they
                                // want, but the toggle is the natural first stop and the
                                // canvas remains uncluttered.
                                customEventHandler.dispatchEvent('openRightDrawer', {});
                                setTimeout(function () {
                                    var toggleBtn = document.querySelector('.drawer-toggle-btn');
                                    toggleBtn === null || toggleBtn === void 0 ? void 0 : toggleBtn.focus();
                                }, 100);
                            }
                            else if (pieces.length > 0) {
                                // Pieces placed → move focus to the first newly-created
                                // piece on the canvas. setTimeout lets React commit the
                                // piece before we query the DOM.
                                var firstNewId_1 = pieces[0].data.id;
                                setTimeout(function () {
                                    var _a;
                                    var wrapper = document.querySelector("[data-manipulative-id=\"".concat(firstNewId_1, "\"]"));
                                    var target = (_a = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('button:not([disabled]):not([tabindex="-1"]), [tabindex="0"]')) !== null && _a !== void 0 ? _a : wrapper;
                                    target === null || target === void 0 ? void 0 : target.focus();
                                }, 100);
                            }
                        }
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    }, onCancel: function () {
                        setSelectedManipulativeId('');
                        setManiplulatorComponentVisible(false);
                        setActiveManipulator('');
                    } }));
            }
            case 'spinners': {
                var spinnerSelectedData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                return _jsx(SpinnerManipulativePopup, { data: spinnerSelectedData });
            }
            case 'numberLines': {
                var nlData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                return _jsx(NumberLineManipulativePopup, { data: nlData });
            }
            case 'patternShapesLibrary':
                return _jsx(PatternShapesManipulativePopup, {});
            case 'clearWorkConfirmation':
                return _jsx(PatternOutlineConfirmationPopup, { mode: "clearWork" });
            case 'patternOutlineConfirmation':
                return _jsx(PatternOutlineConfirmationPopup, { mode: "outline" });
            case 'patternClearOutlineConfirmation':
                return _jsx(PatternClearOutlineConfirmationPopup, {});
            case 'outlineConfirmation': {
                return (_jsxs("div", { style: {
                        textAlign: 'center',
                        padding: '40px 20px',
                        minWidth: '400px',
                    }, children: [_jsx("h3", { style: {
                                marginBottom: '30px',
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#1a2956',
                                lineHeight: '1.5',
                            }, children: t('text.changeToOutlineConfirmHeading') }), _jsxs("div", { style: { display: 'flex', gap: '15px', justifyContent: 'center' }, children: [_jsx("button", { onClick: function () {
                                        setManiplulatorComponentVisible(false);
                                        setActiveManipulator('');
                                    }, style: {
                                        padding: '10px 24px',
                                        backgroundColor: '#1a2956',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '15px',
                                        minWidth: '100px',
                                    }, children: t('text.cancel') }), _jsx("button", { onClick: function () {
                                        var selectedItems = manipulative.filter(function (m) { var _a; return selectedTool.includes((_a = m.data.id) !== null && _a !== void 0 ? _a : '') && m.data.type === 'patternShapes'; });
                                        if (selectedItems.length > 0) {
                                            var outlineGroupId_1 = "manipulativeGroup_".concat(uuidv4().replace(/-/g, ''));
                                            var oldData_1 = [];
                                            var newData_1 = [];
                                            setManipulative(function (prev) {
                                                var updated = prev.map(function (item) {
                                                    var _a;
                                                    if (selectedItems.some(function (s) { return s.data.id === item.data.id; })) {
                                                        oldData_1.push(JSON.parse(JSON.stringify(item)));
                                                        var newItem = __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { groupId: outlineGroupId_1, color: 'DARK_NAVY_BLUE', lock: {
                                                                    positioning: true,
                                                                    editing: ((_a = item.data.lock) === null || _a === void 0 ? void 0 : _a.editing) || false,
                                                                } }) });
                                                        newData_1.push(JSON.parse(JSON.stringify(newItem)));
                                                        return newItem;
                                                    }
                                                    return item;
                                                });
                                                // Record state change for undo/redo
                                                if (oldData_1.length > 0) {
                                                    saveState({
                                                        type: outlineGroupId_1,
                                                        id: outlineGroupId_1.split('_')[1],
                                                        propertyName: 'completeData',
                                                        old: JSON.stringify(oldData_1),
                                                        new: JSON.stringify(newData_1),
                                                    });
                                                }
                                                return updated;
                                            });
                                            setSelectedTool([outlineGroupId_1]);
                                        }
                                        setManiplulatorComponentVisible(false);
                                        setActiveManipulator('');
                                    }, style: {
                                        padding: '10px 24px',
                                        backgroundColor: '#1a2956',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '15px',
                                        minWidth: '100px',
                                    }, children: t('text.done') })] })] }));
            }
            default:
                return null;
        }
    };
    var popupAriaLabels = {
        dice: 'Dice configuration',
        cards: 'Cards configuration',
        clocks: 'Clock configuration',
        spinners: 'Spinner configuration',
        numberCharts: 'Number chart configuration',
        numberChartGame: 'Number chart game',
        chartSizeAlert: 'Chart size alert',
        numberFrames: 'Number frames configuration',
        arrayModels: 'Array model configuration',
        coinsAndCounters: 'Coins and counters configuration',
        coloredTiles: t('text.coloredTilesConfigPopup'),
        fractionTiles: 'Fraction tiles configuration',
        moneyPieces: 'Number and money pieces configuration',
        patternShapesLibrary: t('text.patternShapesLibraryAriaLabel'),
        clearWorkConfirmation: t('text.clearWorkConfirmAriaLabel'),
        patternOutlineConfirmation: t('text.patternOutlineConfirmAriaLabel'),
        patternClearOutlineConfirmation: t('text.clearPatternOutlineConfirmAriaLabel'),
        outlineConfirmation: t('text.outlineConfirmAriaLabel'),
    };
    return (
    // <></>
    _jsx(Popup, { isOpen: maniplulatorComponentVisible, onClose: function () {
            if (!selectedManipulativeId.includes('fractionTiles')) {
                setSelectedManipulativeId('');
            }
            setManiplulatorComponentVisible(false);
            setActiveManipulator('');
        }, className: "manipulativePopup ".concat(activeManipulator), ariaLabel: (_a = popupAriaLabels[activeManipulator]) !== null && _a !== void 0 ? _a : 'Configuration', returnFocusTo: activeManipulator === 'fractionTiles' ||
            (activeManipulator === 'coinsAndCounters' && effectiveSelectedId === '')
            ? popupReturnFocusRef
            : undefined, children: renderPopup() }));
};
export default ManipulativePopup;
